import { englishPairScore } from "@/lib/tool-kit/ciphers-misc/english-ngrams";

/**
 * The route cipher: write the message into a grid row by row, then read the grid back
 * along a route — a spiral, down the columns, a snake through the rows.
 *
 * The grid has `columns` cells per row and as many rows as the message needs. The last
 * row may be short; the route simply skips the empty cells, so the receiver rebuilds the
 * same grid from the message length and the column count alone. (A caller that wants a
 * full grid can pad first with `padTokens`.)
 *
 * Everything works on arrays of tokens — one code point per token for a letter route, one
 * word per token for the word routes the Union Army used in the Civil War — so the same
 * code serves both.
 */

export const MIN_COLUMNS = 2;
export const MAX_COLUMNS = 60;

export type RoutePattern =
  | "spiral-cw"
  | "spiral-ccw"
  | "columns"
  | "columns-snake"
  | "rows-snake";

export type RouteCorner =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export interface RouteOptions {
  columns: number;
  pattern: RoutePattern;
  /** The cell the route starts in. */
  corner: RouteCorner;
  /** Read the route backwards, from its last cell to its first. */
  reverse: boolean;
}

export const ROUTE_PATTERNS: readonly RoutePattern[] = [
  "spiral-cw",
  "spiral-ccw",
  "columns",
  "columns-snake",
  "rows-snake",
];

export const ROUTE_CORNERS: readonly RouteCorner[] = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

type Cell = readonly [row: number, column: number];

/** Moves as [row, column] steps: right, down, left, up — clockwise in that order. */
const MOVES: readonly Cell[] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];

/** The direction a clockwise spiral leaves each corner in (index into MOVES). */
const CLOCKWISE_START: Record<RouteCorner, number> = {
  "top-left": 0,
  "top-right": 1,
  "bottom-right": 2,
  "bottom-left": 3,
};

export function gridRowCount(length: number, columns: number): number {
  return Math.max(1, Math.ceil(length / columns));
}

function startCell(corner: RouteCorner, rows: number, columns: number): Cell {
  const row = corner.startsWith("top") ? 0 : rows - 1;
  const column = corner.endsWith("left") ? 0 : columns - 1;
  return [row, column];
}

function spiralCells(
  rows: number,
  columns: number,
  corner: RouteCorner,
  clockwise: boolean
): Cell[] {
  const seen = Array.from({ length: rows }, () =>
    Array.from({ length: columns }, () => false)
  );
  const free = (row: number, column: number) =>
    row >= 0 &&
    row < rows &&
    column >= 0 &&
    column < columns &&
    !seen[row][column];
  const turn = clockwise ? 1 : MOVES.length - 1;
  let direction = CLOCKWISE_START[corner];
  if (!clockwise) {
    direction = (direction + 1) % MOVES.length;
  }
  let [row, column] = startCell(corner, rows, columns);
  const cells: Cell[] = [];
  for (let step = 0; step < rows * columns; step += 1) {
    cells.push([row, column]);
    seen[row][column] = true;
    if (!free(row + MOVES[direction][0], column + MOVES[direction][1])) {
      direction = (direction + turn) % MOVES.length;
    }
    row += MOVES[direction][0];
    column += MOVES[direction][1];
  }
  return cells;
}

/** The sweep patterns, laid out from the top-left corner and flipped afterwards. */
function sweepCells(
  rows: number,
  columns: number,
  pattern: RoutePattern
): Cell[] {
  const cells: Cell[] = [];
  if (pattern === "rows-snake") {
    for (let row = 0; row < rows; row += 1) {
      for (let step = 0; step < columns; step += 1) {
        cells.push([row, row % 2 === 0 ? step : columns - 1 - step]);
      }
    }
    return cells;
  }
  for (let column = 0; column < columns; column += 1) {
    for (let step = 0; step < rows; step += 1) {
      const snaking = pattern === "columns-snake" && column % 2 === 1;
      cells.push([snaking ? rows - 1 - step : step, column]);
    }
  }
  return cells;
}

function routeCells(
  rows: number,
  columns: number,
  options: RouteOptions
): Cell[] {
  const { pattern, corner } = options;
  if (pattern === "spiral-cw" || pattern === "spiral-ccw") {
    return spiralCells(rows, columns, corner, pattern === "spiral-cw");
  }
  const flipRows = corner.startsWith("bottom");
  const flipColumns = corner.endsWith("right");
  return sweepCells(rows, columns, pattern).map(([row, column]) => [
    flipRows ? rows - 1 - row : row,
    flipColumns ? columns - 1 - column : column,
  ]);
}

/**
 * Row-major cell numbers (row × columns + column) in reading order, for a message of
 * `length` tokens. Empty cells in the last row are left out.
 */
export function routeOrder(length: number, options: RouteOptions): number[] {
  const rows = gridRowCount(length, options.columns);
  const order = routeCells(rows, options.columns, options)
    .map(([row, column]) => row * options.columns + column)
    .filter((index) => index < length);
  return options.reverse ? order.reverse() : order;
}

export function routeEncode(tokens: string[], options: RouteOptions): string[] {
  return routeOrder(tokens.length, options).map((index) => tokens[index]);
}

export function routeDecode(tokens: string[], options: RouteOptions): string[] {
  const plain: string[] = Array.from({ length: tokens.length }, () => "");
  for (const [position, index] of routeOrder(
    tokens.length,
    options
  ).entries()) {
    plain[index] = tokens[position];
  }
  return plain;
}

/** Fill the last row with `filler` so the grid is a full rectangle. */
export function padTokens(
  tokens: string[],
  columns: number,
  filler: string
): string[] {
  if (tokens.length === 0) {
    return tokens;
  }
  const missing = (columns - (tokens.length % columns)) % columns;
  return [...tokens, ...Array.from({ length: missing }, () => filler)];
}

/** The message as the grid it is written in; `null` marks the empty cells. */
export function routeGrid(
  tokens: string[],
  columns: number
): (string | null)[][] {
  const rows = gridRowCount(tokens.length, columns);
  return Array.from({ length: rows }, (_, row) =>
    Array.from({ length: columns }, (_, column) => {
      const index = row * columns + column;
      return index < tokens.length ? tokens[index] : null;
    })
  );
}

/** The grid again, each cell holding the step (from 1) at which the route reads it. */
export function routeSteps(
  length: number,
  options: RouteOptions
): (number | null)[][] {
  const rows = gridRowCount(length, options.columns);
  const steps: (number | null)[] = Array.from(
    { length: rows * options.columns },
    () => null
  );
  for (const [position, index] of routeOrder(length, options).entries()) {
    steps[index] = position + 1;
  }
  return Array.from({ length: rows }, (_, row) =>
    steps.slice(row * options.columns, (row + 1) * options.columns)
  );
}

export interface RouteCandidate {
  columns: number;
  plain: string[];
  /** English-likeness from common letter pairs and triples; higher is better. */
  score: number;
}

/**
 * Every grid width from 2 up to `maxColumns` (and at most one less than the length) that
 * reads differently along the given route, best-looking first. Meant for letter routes;
 * on a short message the order is a hint.
 */
export function routeCrack(
  tokens: string[],
  base: Omit<RouteOptions, "columns">,
  maxColumns = MAX_COLUMNS
): RouteCandidate[] {
  const limit = Math.min(maxColumns, tokens.length - 1);
  const candidates: RouteCandidate[] = [];
  const seen = new Set<string>();
  for (let columns = MIN_COLUMNS; columns <= limit; columns += 1) {
    const plain = routeDecode(tokens, { ...base, columns });
    const text = plain.join("");
    if (!seen.has(text)) {
      seen.add(text);
      candidates.push({ columns, plain, score: englishPairScore(text) });
    }
  }
  return candidates.sort((a, b) => b.score - a.score || a.columns - b.columns);
}
